import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HeartPulse, Save, Sparkles } from "lucide-react";

// iter942 — Phase override map: mid-point cycle_day per phase.
// Backend get_cycle_modifier() ranges: 1–13 follicular, 14–16 ovulatory, 17+ luteal.
const PHASE_OVERRIDES = {
  menstrual:  { day: 3,  modifier: 0.95, labelBg: "Менструална",  hintBg: "Ниска чувствителност — обичайно −5% DIL." },
  follicular: { day: 8,  modifier: 0.95, labelBg: "Фоликуларна",  hintBg: "Оптимална чувствителност — базов DIL." },
  ovulatory:  { day: 15, modifier: 1.00, labelBg: "Овулаторна",   hintBg: "Балансирана фаза около овулация." },
  luteal:     { day: 22, modifier: 1.10, labelBg: "Лутеална",     hintBg: "По-строг контрол на въглехидратите (+10% на DIL toleransa)." },
};

/**
 * iter354 — Auto-cycle tracker settings.
 *
 * Lets a female user enter the start date of her last menstruation +
 * average cycle length ONCE. From then on the backend automatically
 * derives `cycle_day` for the Plate Simulator and DIL ceiling — no need
 * to update at every meal.
 *
 * Backend: PUT /api/dil/cycle accepts {lastPeriodDate, avgCycleLength}.
 */
export default function AutoCycleSettings() {
  const { API, user, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const profile = user?.profile || {};
  const [lastPeriodDate, setLastPeriodDate] = useState(profile.lastPeriodDate || "");
  const [avgCycleLength, setAvgCycleLength] = useState(profile.avgCycleLength || 28);
  const [saving, setSaving] = useState(false);
  // iter942 — explicit phase override (independent of last-period date).
  const [phaseOverride, setPhaseOverride] = useState(null);
  const [dilPreview, setDilPreview] = useState(null); // { ceiling, modifier, phase }

  useEffect(() => {
    setLastPeriodDate(profile.lastPeriodDate || "");
    setAvgCycleLength(profile.avgCycleLength || 28);
  }, [profile.lastPeriodDate, profile.avgCycleLength]);

  // Derive the auto cycle_day locally for instant feedback (server still authoritative).
  const computeCycleDay = useCallback(() => {
    if (!lastPeriodDate) return null;
    const lp = new Date(lastPeriodDate);
    if (Number.isNaN(lp.getTime())) return null;
    const today = new Date();
    const diff = Math.floor((today - lp) / 86400000);
    if (diff < 0) return null;
    const len = parseInt(avgCycleLength, 10) || 28;
    return (diff % len) + 1;
  }, [lastPeriodDate, avgCycleLength]);

  const cycleDay = computeCycleDay();
  const phase = cycleDay == null ? null
    : cycleDay <= 5 ? "menstrual"
    : cycleDay <= 13 ? "follicular"
    : cycleDay <= 16 ? "ovulatory"
    : "luteal";
  const phaseBg = { menstrual: "менструална", follicular: "фоликуларна",
                    ovulatory: "овулаторна", luteal: "лутеална" };

  const save = async () => {
    if (!lastPeriodDate) { toast.warning("Въведете дата на последна менструация"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/dil/cycle`, {
        lastPeriodDate,
        avgCycleLength: parseInt(avgCycleLength, 10) || 28,
      });
      toast.success("Автоматичен цикъл-трекер активиран");
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Грешка при запис");
    } finally { setSaving(false); }
  };

  // iter942 — Explicit phase override → sends cycleDay = mid-point of phase.
  const applyPhase = async (phaseKey) => {
    const cfg = PHASE_OVERRIDES[phaseKey];
    if (!cfg) return;
    setPhaseOverride(phaseKey);
    setSaving(true);
    try {
      await axios.put(`${API}/dil/cycle`, { cycleDay: cfg.day });
      // Fetch fresh DIL to show live preview.
      const { data } = await axios.get(`${API}/dil/profile`);
      const ceiling = data?.dil?.personalized_dil;
      const mod     = data?.dil?.cycle_modifier;
      setDilPreview({
        ceiling: ceiling != null ? Number(ceiling).toFixed(1) : null,
        modifier: mod != null ? Number(mod).toFixed(2) : cfg.modifier.toFixed(2),
        phase:    cfg.labelBg,
      });
      toast.success(`Фаза → ${cfg.labelBg} · DIL ${ceiling?.toFixed?.(1) ?? "—"} g`);
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Грешка при задаване на фаза");
    } finally { setSaving(false); }
  };

  return (
    <Card className={isDark ? "bg-gray-800 border-gray-700" : "card-elevated border-0"}
          data-testid="auto-cycle-settings">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <HeartPulse className="w-5 h-5 text-rose-500" />
          <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            Авто-цикъл трекер
          </h3>
        </div>
        <p className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          Въведете датата на последната менструация веднъж — приложението автоматично
          ще изчислява текущия ден от цикъла за DIL изчисления и Plate Simulator.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Дата на последна менструация</label>
            <Input
              type="date"
              value={lastPeriodDate}
              onChange={(e) => setLastPeriodDate(e.target.value)}
              data-testid="auto-cycle-last-period-input"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Средна дължина (дни)</label>
            <Input
              type="number"
              min="20"
              max="40"
              value={avgCycleLength}
              onChange={(e) => setAvgCycleLength(e.target.value)}
              data-testid="auto-cycle-length-input"
            />
          </div>
        </div>

        {cycleDay && (
          <div className={`rounded-lg p-3 ${isDark ? "bg-rose-500/10" : "bg-rose-50"}`}
               data-testid="auto-cycle-preview">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-rose-500 font-bold">
                  Текущ ден от цикъл
                </p>
                <p className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                  Ден {cycleDay}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-rose-500 font-bold">Фаза</p>
                <p className={`text-sm font-bold ${isDark ? "text-rose-200" : "text-rose-700"} capitalize`}>
                  {phaseBg[phase] || phase}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* iter942 — Ръчен phase override (за ановулация или нередовен цикъл) */}
        <div className="pt-2 border-t border-dashed border-gray-200 dark:border-gray-700" data-testid="cycle-phase-override">
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-purple-500" />
            <p className={`text-[11px] font-bold uppercase tracking-wider ${isDark ? "text-purple-300" : "text-purple-700"}`}>
              Ръчна фаза (real-time DIL)
            </p>
          </div>
          <p className={`text-[11px] mb-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            За нередовен цикъл или ановулация — избери фазата, в която се
            намираш сега. DIL се преизчислява веднага.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
            {Object.entries(PHASE_OVERRIDES).map(([key, cfg]) => {
              const isActive = phaseOverride === key || (phase === key && !phaseOverride);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => applyPhase(key)}
                  disabled={saving}
                  data-testid={`cycle-phase-btn-${key}`}
                  className={`text-[11px] px-2 py-2 rounded-lg font-semibold border transition
                    ${isActive
                      ? "bg-gradient-to-br from-purple-500 to-rose-500 text-white border-transparent shadow-md"
                      : isDark
                        ? "bg-gray-700/50 border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-purple-50"}`}
                >
                  <div>{cfg.labelBg}</div>
                  <div className={`text-[9px] mt-0.5 ${isActive ? "text-white/80" : "text-gray-400"}`}>
                    ×{cfg.modifier.toFixed(2)}
                  </div>
                </button>
              );
            })}
          </div>
          {dilPreview && (
            <div
              className={`mt-2 rounded-lg p-2.5 text-[11px] ${
                isDark ? "bg-purple-500/10 text-purple-200" : "bg-purple-50 text-purple-800"
              }`}
              data-testid="cycle-phase-dil-preview"
            >
              <span className="font-bold">{dilPreview.phase}</span> →{" "}
              нов DIL таван: <span className="font-extrabold">{dilPreview.ceiling ?? "—"} g</span>
              {" · "}модификатор ×{dilPreview.modifier}
            </div>
          )}
        </div>

        <Button
          onClick={save}
          disabled={saving}
          className="gradient-primary text-white w-full"
          data-testid="auto-cycle-save-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Записва…" : "Запази настройката"}
        </Button>
      </CardContent>
    </Card>
  );
}
